// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Edit distance between package names.
 *
 * Two decisions here are load-bearing and neither is the obvious one.
 *
 * 1. THIS IS TRUE DAMERAU-LEVENSHTEIN, NOT OPTIMAL STRING ALIGNMENT. Almost
 *    every "Damerau-Levenshtein" you find in a npm package is actually OSA,
 *    which forbids editing a substring twice and therefore gives d("ca","abc")
 *    = 3 where the real answer is 2. OSA is NOT A METRIC — it violates the
 *    triangle inequality on exactly that family of inputs — and a BK-tree is
 *    only correct over a metric. Using OSA would make the tree silently drop
 *    real neighbours: not throw, not warn, just fail to return the popular
 *    package the user was one keystroke away from, which is the entire product.
 *
 * 2. COSTS ARE IN HALF-UNITS SO KEYBOARD ADJACENCY CAN BE CHEAPER WITHOUT
 *    LEAVING THE INTEGERS. An ordinary edit costs 2. Substituting a key for one
 *    physically next to it on the layout costs 1, because `reqeusts` and
 *    `rewuests` are not equally likely slips. The character cost function is
 *    itself a metric on {0,1,2} (any two non-zero costs sum to at least 2,
 *    which is the maximum), so the string distance stays a metric and the tree
 *    stays correct. Integers also keep the tree's bucket keys exact — a
 *    float-keyed BK-tree is a rounding bug waiting to happen.
 */

/** An ordinary insert, delete, substitution or transposition. */
export const UNIT = 2;
/** A substitution between two keys that are physically adjacent on the layout. */
export const ADJACENT = 1;

/*
 * QWERTY adjacency, including the three characters that are legal in package
 * names and sit next to letters: `-`, `.` and `_`. Digits are included because
 * `base64` / `base65` is a real slip. This is a physical-layout claim, not a
 * linguistic one, and it is deliberately conservative: only true neighbours on
 * the same or vertically neighbouring row.
 */
const ROWS = ['1234567890-', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
const ROW_OFFSET = [0, 0.5, 0.9, 1.4];

function buildAdjacency(): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    if (a === b) return;
    if (!map.has(a)) map.set(a, new Set());
    map.get(a)!.add(b);
  };
  for (let r = 0; r < ROWS.length; r++) {
    for (let c = 0; c < ROWS[r].length; c++) {
      const ch = ROWS[r][c];
      if (c > 0) add(ch, ROWS[r][c - 1]);
      if (c < ROWS[r].length - 1) add(ch, ROWS[r][c + 1]);
      for (const dr of [-1, 1]) {
        const rr = r + dr;
        if (rr < 0 || rr >= ROWS.length) continue;
        // Rows are staggered; a key sits between two keys on the row below.
        const shift = ROW_OFFSET[rr] - ROW_OFFSET[r];
        for (const cc of [Math.floor(c - shift), Math.ceil(c - shift)]) {
          if (cc >= 0 && cc < ROWS[rr].length) add(ch, ROWS[rr][cc]);
        }
      }
    }
  }
  // Separators are interchangeable in practice — `python-dateutil` versus
  // `python_dateutil` is a naming-convention slip, not a typing one, and PyPI
  // itself normalises them (PEP 503). Treated as adjacent rather than free,
  // because the normalised forms are compared separately.
  for (const a of ['-', '_', '.']) for (const b of ['-', '_', '.']) add(a, b);
  return map;
}

const ADJACENCY = buildAdjacency();

export function areAdjacentKeys(a: string, b: string): boolean {
  return ADJACENCY.get(a)?.has(b) ?? false;
}

function subCost(a: string, b: string, keyboard: boolean): number {
  if (a === b) return 0;
  if (keyboard && areAdjacentKeys(a, b)) return ADJACENT;
  return UNIT;
}

export interface DistanceOptions {
  /** Charge less for a substitution between physically adjacent keys. */
  keyboard?: boolean;
  /**
   * Stop as soon as the distance is known to exceed this (in half-units) and
   * return `max + 1`. The BK-tree calls this a few thousand times per query;
   * without the cutoff a full matrix is computed for names that are obviously
   * unrelated.
   */
  max?: number;
}

/**
 * True Damerau-Levenshtein, in half-units. `UNIT` (2) per ordinary edit,
 * `ADJACENT` (1) for a keyboard-adjacent substitution when `keyboard` is set.
 */
export function distance(a: string, b: string, options: DistanceOptions = {}): number {
  const keyboard = options.keyboard ?? true;
  const max = options.max ?? Number.POSITIVE_INFINITY;
  if (a === b) return 0;

  const la = a.length;
  const lb = b.length;
  if (la === 0) return lb * UNIT;
  if (lb === 0) return la * UNIT;

  // A length difference alone already costs one edit per surplus character, so
  // this bound is exact and lets the tree reject most candidates for free.
  if (Math.abs(la - lb) * UNIT > max) return max + 1;

  const INF = (la + lb) * UNIT;
  // (la+2) x (lb+2) matrix with the extra guard row/column that the
  // unrestricted algorithm needs for the transposition lookback.
  const width = lb + 2;
  const d = new Int32Array((la + 2) * width);
  const at = (i: number, j: number) => i * width + j;

  d[at(0, 0)] = INF;
  for (let i = 0; i <= la; i++) {
    d[at(i + 1, 0)] = INF;
    d[at(i + 1, 1)] = i * UNIT;
  }
  for (let j = 0; j <= lb; j++) {
    d[at(0, j + 1)] = INF;
    d[at(1, j + 1)] = j * UNIT;
  }

  // Last row in which each character of `a` was seen — this table is what makes
  // it unrestricted DL rather than OSA.
  const lastRow = new Map<string, number>();

  for (let i = 1; i <= la; i++) {
    let lastMatchCol = 0;
    let rowBest = INF;
    for (let j = 1; j <= lb; j++) {
      const i1 = lastRow.get(b[j - 1]) ?? 0;
      const j1 = lastMatchCol;
      const cost = subCost(a[i - 1], b[j - 1], keyboard);
      if (cost === 0) lastMatchCol = j;

      const substitution = d[at(i, j)] + cost;
      const insertion = d[at(i + 1, j)] + UNIT;
      const deletion = d[at(i, j + 1)] + UNIT;
      const transposition =
        i1 > 0 && j1 > 0 ? d[at(i1, j1)] + (i - i1 - 1) * UNIT + UNIT + (j - j1 - 1) * UNIT : INF;

      const best = Math.min(substitution, insertion, deletion, transposition);
      d[at(i + 1, j + 1)] = best;
      if (best < rowBest) rowBest = best;
    }
    lastRow.set(a[i - 1], i);
    // Every cell in a later row is at least this row's minimum, so once the
    // whole row is past the cutoff the answer is too.
    if (rowBest > max) return max + 1;
  }

  return d[at(la + 1, lb + 1)];
}

/** Unweighted true Damerau-Levenshtein in whole edits. Used by the tests. */
export function editDistance(a: string, b: string): number {
  return distance(a, b, { keyboard: false }) / UNIT;
}

/**
 * PEP 503 normalisation. `Foo.Bar_baz` and `foo-bar-baz` are the same PyPI
 * project, and comparing the raw strings reports a two-edit difference between
 * a name and itself.
 */
export function normalisePypi(name: string): string {
  return name.replace(/[-_.]+/g, '-').toLowerCase();
}

/** npm names are already lowercase by rule, but a pasted one may not be. */
export function normaliseNpm(name: string): string {
  return name.toLowerCase();
}
